import React, { useEffect, useRef, useState } from "react";
import "./HomeAdmin.scss";
import StatusPage from "./StatusPage";
import {
    AdminCompanyExtended,
    AdminOffer,
    CandidateRow,
    AdminCandidateDetail,
    AdminCandidateListRow,
} from "../admin-types";
import CompaniesList from "../views/CompaniesList";
import CandidatesList from "../views/CandidatesList";
import CompanyDetails from "../views/CompanyDetails";
import OfferDetails from "../views/OfferDetails";
import CandidateView from "../views/CandidateView";

type OffersByCompany = Record<string, AdminOffer[]>;
type CandidatesByOffer = Record<string, CandidateRow[]>;
type CandidateDetailsById = Record<string, AdminCandidateDetail>;

export default function HomeAdmin() {
    const [selectedCompany, setSelectedCompany] =
        useState<AdminCompanyExtended | null>(null);
    const [selectedOffer, setSelectedOffer] = useState<AdminOffer | null>(null);
    const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(
        null
    );
    const [activeView, setActiveView] = useState<
        "companies" | "candidates" | "status"
    >("companies");

    // Caches
    const companiesCache = useRef<AdminCompanyExtended[] | null>(null);
    const offersCache = useRef<OffersByCompany>({});
    const candidatesCache = useRef<CandidatesByOffer>({});
    const candidatesListCache = useRef<AdminCandidateListRow[] | null>(null);
    const candidateDetailsCache = useRef<CandidateDetailsById>({});

    // Refresh keys
    const [refreshCompaniesKey, setRefreshCompaniesKey] = useState(0);
    const [refreshCompanyOffersKey, setRefreshCompanyOffersKey] = useState(0);
    const [refreshOfferCandidatesKey, setRefreshOfferCandidatesKey] = useState(0);
    const [refreshCandidateKey, setRefreshCandidateKey] = useState(0);
    const [refreshCandidatesListKey, setRefreshCandidatesListKey] = useState(0);

    // Reset deeper levels when going up
    useEffect(() => {
        setSelectedOffer(null);
        setSelectedCandidateId(null);
    }, [selectedCompany?.id]);

    useEffect(() => {
        setSelectedCandidateId(null);
    }, [selectedOffer?.id]);

    const goBack = () => {
        if (selectedCandidateId) {
            setSelectedCandidateId(null);
        } else if (selectedOffer) {
            setSelectedOffer(null);
        } else if (selectedCompany) {
            setSelectedCompany(null);
        }
    };

    const refreshCurrent = () => {
        if (selectedCandidateId) {
            setRefreshCandidateKey((k) => k + 1);
            return;
        }
        if (activeView === "candidates") {
            setRefreshCandidatesListKey((k) => k + 1);
            return;
        }
        if (selectedOffer) {
            setRefreshOfferCandidatesKey((k) => k + 1);
        } else if (selectedCompany) {
            setRefreshCompanyOffersKey((k) => k + 1);
        } else {
            setRefreshCompaniesKey((k) => k + 1);
        }
    };

    const activeCandidateRow =
        selectedCandidateId
            ? selectedOffer
                ? candidatesCache.current[selectedOffer.id]?.find(
                    (r) => r.candidate_id === selectedCandidateId
                )
                : candidatesListCache.current?.find(
                    (r) =>
                        r.candidate_id === selectedCandidateId || r.id === selectedCandidateId
                )
            : null;

    const candidateLabel = activeCandidateRow
        ? `${activeCandidateRow.first_name} ${activeCandidateRow.last_name}`
        : "Candidato";

    const setView = (view: "companies" | "candidates" | "status") => {
        setActiveView(view);
        setSelectedCompany(null);
        setSelectedOffer(null);
        setSelectedCandidateId(null);
    };

    return (
        <div className="admin-container">
            <div className="admin-content">
                {/* --- HEADER SECTION --- */}
                <div style={{ marginBottom: "24px" }}>
                    {/* Top Row: Title & Tabs */}
                    <div className="admin-header" style={{ marginBottom: "16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                            <h1>
                                {activeView === "status"
                                    ? "Estado del sistema"
                                    : "Zas!Jobs Panel Admin"}
                            </h1>

                            <div className="view-toggle">
                                <button
                                    onClick={() => setView("companies")}
                                    className={`tab-button ${
                                        activeView === "companies" ? "is-active" : ""
                                    }`}
                                >
                                    Empresas
                                </button>
                                <button
                                    onClick={() => setView("candidates")}
                                    className={`tab-button ${
                                        activeView === "candidates" ? "is-active" : ""
                                    }`}
                                >
                                    Candidatos
                                </button>
                                <button
                                    onClick={() => setView("status")}
                                    className={`tab-button ${
                                        activeView === "status" ? "is-active" : ""
                                    }`}
                                >
                                    Estado del sistema
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Second Row: Dashboard Toolbar */}
                    {activeView !== "status" && (
                        <div className="dashboard-toolbar">
                            {/* Breadcrumbs */}
                            <nav className="breadcrumb">
                                {activeView === "companies" ? (
                                    <>
                                        <button
                                            onClick={() => {
                                                setSelectedCompany(null);
                                                setSelectedOffer(null);
                                                setSelectedCandidateId(null);
                                            }}
                                            className="breadcrumb-btn"
                                        >
                                            Inicio
                                        </button>

                                        {selectedCompany && (
                                            <>
                                                <span className="breadcrumb-separator">/</span>
                                                <button
                                                    onClick={() => {
                                                        setSelectedOffer(null);
                                                        setSelectedCandidateId(null);
                                                    }}
                                                    className={`breadcrumb-btn ${
                                                        !selectedOffer ? "breadcrumb-active" : ""
                                                    }`}
                                                >
                                                    {selectedCompany.name}
                                                </button>
                                            </>
                                        )}

                                        {selectedOffer && (
                                            <>
                                                <span className="breadcrumb-separator">/</span>
                                                <button
                                                    onClick={() => setSelectedCandidateId(null)}
                                                    className={`breadcrumb-btn ${
                                                        !selectedCandidateId ? "breadcrumb-active" : ""
                                                    }`}
                                                >
                                                    {selectedOffer.position}
                                                </button>
                                            </>
                                        )}

                                        {selectedCandidateId && (
                                            <>
                                                <span className="breadcrumb-separator">/</span>
                                                <span className="breadcrumb-active">
                                                    {candidateLabel}
                                                </span>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <button
                                            onClick={() => setSelectedCandidateId(null)}
                                            className={`breadcrumb-btn ${
                                                !selectedCandidateId ? "breadcrumb-active" : ""
                                            }`}
                                        >
                                            Candidatos
                                        </button>
                                        {selectedCandidateId && (
                                            <>
                                                <span className="breadcrumb-separator">/</span>
                                                <span className="breadcrumb-active">
                                                    {candidateLabel}
                                                </span>
                                            </>
                                        )}
                                    </>
                                )}
                            </nav>

                            {/* Actions Toolbar */}
                            <div className="header-actions">
                                {(selectedCompany || selectedOffer || selectedCandidateId) && (
                                    <button onClick={goBack} className="action-btn ghost">
                                        ⬅ Atrás
                                    </button>
                                )}
                                <button onClick={refreshCurrent} className="action-btn solid">
                                    ↻ Actualizar
                                </button>
                                {(selectedCompany || selectedOffer) && (
                                    <button
                                        onClick={() => {
                                            setSelectedCompany(null);
                                            setSelectedOffer(null);
                                            setSelectedCandidateId(null);
                                        }}
                                        className="action-btn outline"
                                    >
                                        Reiniciar
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* --- CONTENT SECTION --- */}
                {activeView === "companies" ? (
                    <div className="grid">
                        {!selectedCompany && (
                            <CompaniesList
                                onSelectCompany={setSelectedCompany}
                                cached={companiesCache.current}
                                onCache={(data) => (companiesCache.current = data)}
                                refreshKey={refreshCompaniesKey}
                            />
                        )}

                        {selectedCompany && !selectedOffer && (
                            <CompanyDetails
                                company={selectedCompany}
                                onSelectOffer={setSelectedOffer}
                                cachedOffers={offersCache.current[selectedCompany.id]}
                                onCache={(id, data) => (offersCache.current[id] = data)}
                                refreshKey={refreshCompanyOffersKey}
                            />
                        )}

                        {selectedOffer && !selectedCandidateId && (
                            <OfferDetails
                                offer={selectedOffer}
                                onSelectCandidate={(row) =>
                                    (() => {
                                        const nextId = row.user_id || row.candidate_id;
                                        console.log("[Admin] select offer candidate", {
                                            user_id: row.user_id,
                                            candidate_id: row.candidate_id,
                                            nextId,
                                        });
                                        setSelectedCandidateId(nextId);
                                    })()
                                }
                                cachedRows={candidatesCache.current[selectedOffer.id]}
                                onCache={(id, data) => (candidatesCache.current[id] = data)}
                                refreshKey={refreshOfferCandidatesKey}
                            />
                        )}

                        {selectedCandidateId && (
                            <CandidateView
                                candidateId={selectedCandidateId}
                                cachedRows={candidatesCache.current[selectedOffer!.id]}
                                cached={candidateDetailsCache.current[selectedCandidateId]}
                                onCache={(id, d) => (candidateDetailsCache.current[id] = d)}
                                refreshKey={refreshCandidateKey}
                            />
                        )}
                    </div>
                ) : activeView === "candidates" ? (
                    <div className="grid">
                        {!selectedCandidateId && (
                            <CandidatesList
                                onSelectCandidate={(row) =>
                                    (() => {
                                        const nextId = row.user_id || row.candidate_id || row.id;
                                        console.log("[Admin] select candidate", {
                                            user_id: row.user_id,
                                            candidate_id: row.candidate_id,
                                            id: row.id,
                                            nextId,
                                        });
                                        setSelectedCandidateId(nextId);
                                    })()
                                }
                                cached={candidatesListCache.current}
                                onCache={(data) => (candidatesListCache.current = data)}
                                refreshKey={refreshCandidatesListKey}
                            />
                        )}
                        {selectedCandidateId && (
                            <CandidateView
                                candidateId={selectedCandidateId}
                                cachedRows={candidatesListCache.current || undefined}
                                cached={candidateDetailsCache.current[selectedCandidateId]}
                                onCache={(id, d) => (candidateDetailsCache.current[id] = d)}
                                refreshKey={refreshCandidateKey}
                            />
                        )}
                    </div>
                ) : (
                    <StatusPage />
                )}
            </div>
        </div>
    );
}
